/**
 * ``useAnnotationDrag`` — drag an annotation to move it with the **hand** tool
 * (ADR-034 — geometry move; hand tool batch 5 #5). With the palette **hand**
 * (move) tool active, the cursor becomes a **grab** hand over any shape and a
 * press-drag moves it (a plain click selects it for config instead). Dragging is
 * gated on the hand tool — an explicit move mode, so a shape is never moved by
 * an incidental click. Per kind:
 *
 *   - **marker / circle / polygon** → the whole shape translates (a circle moves
 *     its centre; ``radius_m`` is unchanged).
 *   - **line** → grabbing the **body** translates the whole line; grabbing **a
 *     vertex** (within a pixel threshold) moves only that vertex.
 *
 * Canonical MapLibre draggable pattern: ``mousedown`` on a shape (hand active)
 * ``preventDefault``s the map pan; ``mousemove`` writes the live geometry to the
 * drag store (the render + label layers preview it via ``useAnnotationsWithDrag``
 * so the shape *and* its readout follow the cursor); ``mouseup`` (window — robust
 * to an off-canvas release) ``PUT``s once. **Escape** cancels (revert, no PUT).
 * A press with no move is a plain click — no PUT.
 *
 * Renders nothing. WebGL-only — verified in the §5.11 visual pass; the geometry
 * math (annotation-drag-geometry) + the store are unit-tested.
 *
 * Cross-references:
 *   - ADR-034 (D7 — geometry move, un-deferred 2026-06-11); feature spec §8.
 *   - @/annotations/{annotation-drag-geometry, annotation-drag-store}.
 */
import { useEffect, useRef } from "react";
import type { Map as MapLibreMap, MapMouseEvent } from "maplibre-gl";
import { isMapUsable } from "@/map/map-ready";
import { useAnnotations, useUpdateAnnotation } from "@/annotations/use-annotations";
import type { Annotation } from "@/annotations/use-annotations";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { useAnnotationDragStore } from "@/annotations/annotation-drag-store";
import {
  translateGeometry,
  translateVertex,
  insertVertexAt,
  deleteVertex,
  radiusForCursor,
} from "@/annotations/annotation-drag-geometry";
import {
  resolveEditPress,
  isVertexDelete,
  type EditAction,
  type HandleHit,
} from "@/annotations/annotation-edit-hit";
import { HANDLE_LAYER_ID } from "@/map/annotation-handle-layer";
import type { HandleKind } from "@/annotations/annotation-handles";

/** Pixel box around the cursor used to query the handle layer. Handles are
 *  ~5px radius, so a small box makes them grabbable without demanding
 *  pixel-perfect aim. */
const HANDLE_QUERY_PX = 8;

interface DragBookkeeping {
  readonly annotationId: string;
  /** The geometry as it was at press time — every transform is applied to this
   *  rather than to the live one, so a drag is idempotent per frame. */
  readonly original: Annotation["geometry"];
  readonly grabLng: number;
  readonly grabLat: number;
  /** Where the press landed, for the click-vs-drag guard on vertex delete. */
  readonly pressPx: { readonly x: number; readonly y: number };
  /** What this gesture does, resolved once at press time. */
  readonly action: EditAction;
  /** For insert-vertex: the index the new corner took, so subsequent moves
   *  address it rather than re-inserting on every mouse-move. */
  readonly insertedIndex: number;
  /** For resize-circle: the live radius, persisted on release. */
  radiusM: number | null;
}

/** Mount annotation drag-to-move on the map. Call once with the loaded map. */
export function useAnnotationDrag(map: MapLibreMap | null): void {
  const updateAnnotation = useUpdateAnnotation();
  const annotationsQuery = useAnnotations();

  // Latest-refs so the once-registered handlers see current data + mutation.
  const annotationsRef = useRef(annotationsQuery.data);
  const mutateRef = useRef(updateAnnotation.mutate);
  useEffect(() => {
    annotationsRef.current = annotationsQuery.data;
  });
  useEffect(() => {
    mutateRef.current = updateAnnotation.mutate;
  });

  useEffect(() => {
    if (map === null || !isMapUsable(map)) {
      return undefined;
    }
    let drag: DragBookkeeping | null = null;
    let cursorOwned = false;
    const setCursor = (cursor: string): void => {
      map.getCanvas().style.cursor = cursor;
      cursorOwned = cursor !== "";
    };

    /** The annotation under the cursor (topmost render-layer feature carrying an
     *  ``annotation_id``), or null. */
    const annotationUnderCursor = (point: { x: number; y: number }): Annotation | null => {
      const hit = map
        .queryRenderedFeatures(point as Parameters<typeof map.queryRenderedFeatures>[0])
        .find((feature) => typeof feature.properties?.annotation_id === "string");
      const id = hit?.properties?.annotation_id;
      if (typeof id !== "string") {
        return null;
      }
      return annotationsRef.current?.find((a) => a.annotation_id === id) ?? null;
    };

    /** True while the palette Edit tool is active — the only mode in
     *  which a press-drag moves a shape (batch 5, #5). */
    const editToolActive = (): boolean => useAnnotationToolStore.getState().activeTool === "edit";

    /** Handles under the cursor, read off the handle layer's rendered features.
     *  Querying the LAYER (rather than re-projecting geometry) means the hit
     *  test agrees with what the operator can actually see. */
    const handlesUnderCursor = (point: { x: number; y: number }): HandleHit[] => {
      if (map.getLayer(HANDLE_LAYER_ID) === undefined) {
        return [];
      }
      const box: [[number, number], [number, number]] = [
        [point.x - HANDLE_QUERY_PX, point.y - HANDLE_QUERY_PX],
        [point.x + HANDLE_QUERY_PX, point.y + HANDLE_QUERY_PX],
      ];
      return map.queryRenderedFeatures(box, { layers: [HANDLE_LAYER_ID] }).flatMap((feature) => {
        const props = feature.properties ?? {};
        if (typeof props.annotation_id !== "string" || typeof props.handle_kind !== "string") {
          return [];
        }
        return [
          {
            annotationId: props.annotation_id,
            kind: props.handle_kind as HandleKind,
            vertexIndex: Number(props.vertex_index ?? 0),
          },
        ];
      });
    };

    const onMouseDown = (event: MapMouseEvent): void => {
      if (!editToolActive()) {
        return; // editing only with the Edit tool active
      }
      const body = annotationUnderCursor(event.point);
      const action = resolveEditPress({
        handles: handlesUnderCursor(event.point),
        bodyAnnotationId: body?.annotation_id ?? null,
      });
      if (action === null) {
        return; // pressed on empty space — let the map pan
      }
      const annotation = annotationsRef.current?.find(
        (a) => a.annotation_id === action.annotationId,
      );
      if (annotation === undefined) {
        return;
      }

      // An insert lands its new corner immediately, at the midpoint the handle
      // marked, so the rest of the gesture is an ordinary vertex drag on it.
      let original = annotation.geometry;
      let insertedIndex = -1;
      if (action.type === "insert-vertex") {
        original = insertVertexAt(original, action.segmentIndex, [
          event.lngLat.lng,
          event.lngLat.lat,
        ]);
        insertedIndex = action.segmentIndex + 1;
      }

      drag = {
        annotationId: annotation.annotation_id,
        original,
        grabLng: event.lngLat.lng,
        grabLat: event.lngLat.lat,
        pressPx: { x: event.point.x, y: event.point.y },
        action,
        insertedIndex,
        radiusM: annotation.radius_m ?? null,
      };
      event.preventDefault(); // suppress the map pan for this gesture
      setCursor("grabbing");
    };

    const onMouseMove = (event: MapMouseEvent): void => {
      if (drag !== null) {
        const dLng = event.lngLat.lng - drag.grabLng;
        const dLat = event.lngLat.lat - drag.grabLat;
        let geometry = drag.original;
        switch (drag.action.type) {
          case "move-vertex":
            geometry = translateVertex(drag.original, drag.action.vertexIndex, dLng, dLat);
            break;
          case "insert-vertex":
            // The corner was inserted at press; from here it drags like any other.
            geometry = translateVertex(drag.original, drag.insertedIndex, dLng, dLat);
            break;
          case "resize-circle": {
            // The centre is fixed and the radius follows the cursor — the
            // geometry (a centre Point) does not change at all.
            const centre =
              drag.original.type === "Point" ? (drag.original.coordinates as number[]) : null;
            if (centre !== null) {
              drag.radiusM = radiusForCursor(centre, [event.lngLat.lng, event.lngLat.lat]);
            }
            break;
          }
          default:
            geometry = translateGeometry(drag.original, dLng, dLat);
        }
        useAnnotationDragStore.getState().setDrag({
          annotationId: drag.annotationId,
          geometry,
          radiusM: drag.action.type === "resize-circle" ? drag.radiusM : undefined,
        });
        return;
      }
      // Not dragging — show the grab affordance over any shape while the hand
      // (move) tool is active.
      if (!editToolActive()) {
        if (cursorOwned) {
          setCursor("");
        }
        return;
      }
      if (annotationUnderCursor(event.point) !== null) {
        setCursor("grab");
      } else if (cursorOwned) {
        setCursor("");
      }
    };

    /** Persist a shape's new geometry / radius (one PUT per gesture). */
    const persist = (
      annotation: Annotation,
      geometry: Annotation["geometry"],
      radiusM: number | null,
    ): void => {
      mutateRef.current({
        annotationId: annotation.annotation_id,
        body: {
          kind: annotation.kind,
          geometry,
          radius_m: radiusM,
          style: annotation.style,
          label: annotation.label,
        },
      });
    };

    const endDrag = (commit: boolean, releasePx?: { x: number; y: number }): void => {
      if (drag === null) {
        return;
      }
      const live = useAnnotationDragStore.getState().drag;
      const annotation = annotationsRef.current?.find(
        (a) => a.annotation_id === drag?.annotationId,
      );

      // A still press-release on a VERTEX handle is a click, and a click on a
      // vertex deletes it (REQ-UI-138). Guarded on pointer movement so a
      // twitchy mouse cannot turn an intended move into an unrecoverable
      // delete — ADR-034 D7 defers undo, so the only recovery is redrawing.
      const stillClick =
        releasePx !== undefined && drag !== null && isVertexDelete(drag.pressPx, releasePx);
      if (commit && annotation !== undefined && drag.action.type === "move-vertex" && stillClick) {
        const reduced = deleteVertex(annotation.geometry, drag.action.vertexIndex);
        if (reduced !== null) {
          persist(annotation, reduced, annotation.radius_m ?? null);
        }
        // reduced === null → the guard refused (a line at 2 vertices, a polygon
        // at 3). Do nothing rather than destroy the shape; annotation delete
        // has its own confirm-dialog path.
        useAnnotationDragStore.getState().clearDrag();
        setCursor("");
        drag = null;
        return;
      }

      if (commit && annotation !== undefined && drag.action.type === "resize-circle") {
        // A resize changes radius_m, not geometry — the centre never moved.
        if (drag.radiusM !== null) {
          persist(annotation, annotation.geometry, drag.radiusM);
        }
      } else if (commit && live !== null && annotation !== undefined) {
        // Moved / reshaped / inserted → persist the new geometry.
        persist(annotation, live.geometry, annotation.radius_m ?? null);
      } else if (commit && live === null && drag.action.type === "translate") {
        // A press with no move on the shape body = a click → select it for config.
        useAnnotationToolStore.getState().setSelectedId(drag.annotationId);
      }
      useAnnotationDragStore.getState().clearDrag();
      setCursor("");
      drag = null;
    };

    const onWindowMouseUp = (event: MouseEvent): void => {
      // The release point drives the click-vs-drag guard on vertex delete.
      // Read off the window event and converted to canvas-relative pixels,
      // because a release can land outside the canvas.
      const rect = map.getCanvas().getBoundingClientRect();
      endDrag(true, { x: event.clientX - rect.left, y: event.clientY - rect.top });
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && drag !== null) {
        endDrag(false);
      }
    };

    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);
    window.addEventListener("mouseup", onWindowMouseUp);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      if (isMapUsable(map)) {
        map.off("mousedown", onMouseDown);
        map.off("mousemove", onMouseMove);
      }
      window.removeEventListener("mouseup", onWindowMouseUp);
      window.removeEventListener("keydown", onKeyDown);
      useAnnotationDragStore.getState().clearDrag();
    };
  }, [map]);
}
